import { useCallback, useEffect, useState } from 'react';

import { listBookmarks, type BookmarkListFilters } from '@/api/bookmarks';
import type { Bookmark } from '@/types/models';

interface State {
  data: Bookmark[];
  loading: boolean;
  error: string | null;
}

/**
 * Pass `null` for `filters` to disable fetching entirely (used when the
 * Project Bookmarks tab has no project selected — we don't want to hit the
 * API with a placeholder UUID just to be told 400).
 */
export function useBookmarks(filters: BookmarkListFilters | null, refreshKey: number) {
  const [state, setState] = useState<State>({ data: [], loading: filters !== null, error: null });

  // Key the effect on the JSON form of `filters` so that structurally-equal
  // filters across re-renders don't cause re-fetches. We deliberately do not
  // include `filters` itself in the dep array — its identity changes every
  // render but its content rarely does.
  const filtersKey = filters === null ? null : JSON.stringify(filters);

  const refetch = useCallback(async () => {
    if (filtersKey === null) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const parsed = JSON.parse(filtersKey) as BookmarkListFilters;
      const { bookmarks } = await listBookmarks(parsed);
      setState({ data: bookmarks, loading: false, error: null });
    } catch (err) {
      setState({ data: [], loading: false, error: (err as Error).message });
    }
  }, [filtersKey]);

  useEffect(() => {
    void refetch();
  }, [refetch, refreshKey]);

  return { ...state, refetch };
}
